import { Injectable } from '@angular/core';
import { HttpClient, HttpEvent, HttpRequest } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../../environments/environment';
import { FormSearch } from '../../models/dashboard/FormStatus';
import { BaseDashboard } from '../../models/dashboard/BaseDashboard';
import { UtilityService } from '../utility.service';
import { PoRollbackView } from 'app/models/form/PoRollbackView';
import { DynamicFormUtilityServices } from '../dynamic-form-utility.service';

@Injectable({
	providedIn: 'root',
})
export class PoRollbackFormService {
	private readonly urlPath = 'po-rollback-form';

	constructor(
		private httpClient: HttpClient,
		private utilityService: UtilityService,
		private dynamicFormUtilityServices: DynamicFormUtilityServices
	) {}

	createForm(clientId: string, request: any, prefix: string): Observable<any> {
		return this.httpClient
			.post<any>(`${environment.apiUrl}forms/clients/${clientId}/${this.urlPath}`, this.setupRequest(request, prefix))
			.pipe();
	}

	private setupRequest(request: any, prefix: string) {
		const formData: FormData = new FormData();
		formData.append('model', JSON.stringify(request));
		this.dynamicFormUtilityServices.appendDocuments(formData, prefix, request);
		return formData;
	}

	updateForm(clientId: string, id: string, request: any, prefix: string): Observable<any> {
		return this.httpClient
			.put<any>(
				`${environment.apiUrl}forms/clients/${clientId}/${this.urlPath}/${id}`,
				this.setupRequest(request, prefix)
			)
			.pipe();
	}

	getForm(id: string, clientId: string): Observable<PoRollbackView> {
		return this.httpClient
			.get<PoRollbackView>(`${environment.apiUrl}forms/clients/${clientId}/${this.urlPath}/${id}`)
			.pipe();
	}

	deleteDocument(clientId: string, formId: any, docId: any): Observable<any> {
		return this.httpClient.delete<any>(
			`${environment.apiUrl}forms/clients/${clientId}/${this.urlPath}/${formId}/docs/${docId}`
		);
	}

	getFormsFor(clientId: string, userId: string, search: FormSearch): Observable<BaseDashboard<any>> {
		const url = `${environment.apiUrl}forms/clients/${clientId}/${
			this.urlPath
		}/users/${userId}?${this.utilityService.objectToQueryString(search)}`;
		return this.httpClient.get<BaseDashboard<any>>(url).pipe();
	}

	rejectForm(
		clientId: string,
		formId: string,
		rejectionReason: { reason: string; workflowId: number }
	): Observable<PoRollbackView> {
		return this.httpClient
			.post<PoRollbackView>(
				`${environment.apiUrl}forms/clients/${clientId}/${this.urlPath}/${formId}/decline`,
				rejectionReason
			)
			.pipe();
	}

	approveForm(clientId: string, formId: string, userId: string): Observable<PoRollbackView> {
		return this.httpClient
			.post<PoRollbackView>(`${environment.apiUrl}forms/clients/${clientId}/${this.urlPath}/${formId}/approve`, {
				userId,
			})
			.pipe();
	}

	sendBack(clientId: string, formId: string): Observable<any> {
		return this.httpClient
			.post<any>(`${environment.apiUrl}forms/clients/${clientId}/${this.urlPath}/${formId}/review`, {})
			.pipe();
	}

	uploadFile(clientId: string, id: any, file: File): Observable<HttpEvent<any>> {
		const formData: FormData = new FormData();
		formData.append('file', file);
		const req = new HttpRequest(
			'POST',
			`${environment.apiUrl}forms/clients/${clientId}/${this.urlPath}/${id}/docs`,
			formData,
			{
				reportProgress: true,
				responseType: 'json',
			}
		);
		return this.httpClient.request(req);
	}

	submitForApproval(clientId: string, id: any, userId: string): Observable<any> {
		return this.httpClient
			.post<any>(`${environment.apiUrl}forms/clients/${clientId}/${this.urlPath}/${id}/approval`, {
				userId,
			})
			.pipe();
	}

	/**
	 * Checks whether an AP invoice exists for the given PO number in the fiscal year
	 * it was rolled into (rolloverForFinancialYear). Returns true if invoices exist
	 * (rollback blocked), false if no invoices exist (rollback allowed).
	 */
	checkPoHasInvoicesInRolledOverYear(
		clientId: string,
		poNumber: string,
		rolledOverFiscalYear: number
	): Observable<boolean> {
		return this.httpClient
			.get<boolean>(
				`${environment.apiUrl}forms/clients/${clientId}/ap-invoice/has-invoice-for-po?poNumber=${encodeURIComponent(
					poNumber
				)}&fiscalYear=${rolledOverFiscalYear}`
			)
			.pipe();
	}

	/**
	 * Gets the original fiscal year for a PO by looking up the Purchase Requisition form.
	 * Returns the fiscal year from the original Purchase Requisition, or the current PO fiscal year if not found.
	 */
	getOriginalFiscalYearForPO(clientId: string, poNumber: string): Observable<number> {
		return this.httpClient
			.get<number>(
				`${environment.apiUrl}forms/clients/${clientId}/${this.urlPath}/po/${encodeURIComponent(
					poNumber
				)}/original-fiscal-year`
			)
			.pipe();
	}

	/**
	 * Gets all rollback forms that contain the specified PO number.
	 */
	getFormsByPoNumber(clientId: string, poNumber: string): Observable<PoRollbackView[]> {
		return this.httpClient
			.get<PoRollbackView[]>(
				`${environment.apiUrl}forms/clients/${clientId}/${this.urlPath}/po/${encodeURIComponent(poNumber)}`
			)
			.pipe();
	}
}
